import React from "react";

import { Add } from "@mui/icons-material";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  TextField,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";

import { renderHighlightedOption } from "@/components/alerts/add-v2/fields/highlightedOption";
import { IncidentTimeRow } from "@/components/alerts/add-v2/fields/IncidentTimeRow";
import { LocationRow } from "@/components/alerts/add-v2/fields/LocationRow";
import { useCrimeTypeOptions } from "@/components/alerts/add-v2/hooks/useOptionLists";
import {
  createIncidentTime,
  createLocation,
} from "@/components/alerts/add-v2/model/defaults";
import { validateIncidentTime } from "@/components/alerts/add-v2/model/incidentTimes";
import {
  locationRowHasContent,
  validateLocationRow,
} from "@/components/alerts/add-v2/model/locationRows";
import {
  BulletinIncidentTime,
  BulletinLocation,
} from "@/components/alerts/add-v2/model/types";
import {
  useBulletinField,
  useBulletinFieldError,
} from "@/components/alerts/add-v2/state/bulletinFormContext";
import { useFinalizeAttempted } from "@/components/alerts/add-v2/validation/finalizeAttempt";
import { IAssociatedCrime } from "@/types";

/**
 * Associated crimes, incident times, locations.
 *
 * Rows are self-managing and every row that exists is required — a user removes a row rather than
 * leaving it blank. Location type is selectable before the address resolves.
 *
 * The three lists are three components rather than three hooks in one body, so editing a location
 * does not re-render the crime autocomplete: each subscribes to its own field only.
 *
 * Phase 2 wired the crime options, the state and the incident-date pickers (`fields/IncidentTimeRow`).
 * The address input is still a placeholder — what replaces it is the open question on README
 * decision 7.
 */

/**
 * The crime picker keeps the production field's configuration, which is more than a multi-select:
 * options are **grouped** by crime category with the group label translated out of the
 * `bulletin/associatedCrime` namespace, the dropdown **stays open** while several are picked, the
 * placeholder reports **how many are selected**, already-picked crimes drop out of the list, and the
 * matched substring is highlighted. The list is long enough that all of it earns its place.
 *
 * `groupBy` relies on the options arriving grouped: `useCrimeTypeOptions` flattens the endpoint's
 * groups in order, so each category's crimes stay contiguous and MUI renders one header each.
 */
const CrimesField: React.FC = () => {
  const { t } = useTranslation([
    "bulletin/create-v2",
    "bulletin/associatedCrime",
  ]);
  const crimeTypes = useCrimeTypeOptions();
  const [associatedCrimes, setAssociatedCrimes] =
    useBulletinField("associatedCrimes");
  const error = useBulletinFieldError("associatedCrimes");

  return (
    <Autocomplete<IAssociatedCrime, true>
      multiple
      options={crimeTypes.options}
      value={associatedCrimes}
      onChange={(_event, next) => setAssociatedCrimes([...next])}
      groupBy={(option) => t(`bulletin/associatedCrime:${option.group}`)}
      // Through the list, not off the value: a crime restored from a bulletin is `{crime, group: ""}`
      // with no name of its own, so reading `option.name` showed the officer the raw key.
      getOptionLabel={crimeTypes.getOptionLabel}
      isOptionEqualToValue={(option, value) => option.crime === value.crime}
      disableCloseOnSelect
      filterSelectedOptions
      autoComplete
      autoHighlight
      clearOnBlur
      openOnFocus
      loading={crimeTypes.loading}
      size="small"
      renderTags={(values, getTagProps) =>
        values.map((value, index) => {
          const { key, ...tagProps } = getTagProps({ index });
          return (
            <Chip
              key={key}
              variant="outlined"
              label={crimeTypes.getOptionLabel(value)}
              {...tagProps}
            />
          );
        })
      }
      renderOption={(optionProps, option, { inputValue }) =>
        renderHighlightedOption(
          optionProps,
          crimeTypes.getOptionLabel(option),
          inputValue,
        )
      }
      renderInput={(params) => (
        <TextField
          {...params}
          required
          error={!!error}
          helperText={error}
          label={t("fields.associated_crimes")}
          placeholder={
            associatedCrimes.length
              ? t("fields.n_selected", { count: associatedCrimes.length })
              : t("fields.select_all_that_apply")
          }
          slotProps={{
            input: {
              ...params.InputProps,
              endAdornment: (
                <>
                  {crimeTypes.loading && (
                    <CircularProgress color="inherit" size={16} />
                  )}
                  {params.InputProps.endAdornment}
                </>
              ),
            },
          }}
        />
      )}
    />
  );
};

/**
 * When a row says what is wrong with it: once it holds something, or once Finalize has been tried.
 *
 * A row nobody has filled in has one problem — that it is empty — and the count in the rail already
 * says so. Marking it red before the officer has done anything with it would light up a blank form.
 */
const IncidentTimesField: React.FC = () => {
  const { t } = useTranslation("bulletin/create-v2");
  const [incidentTimes, setIncidentTimes] = useBulletinField("incidentTimes");
  const finalizeAttempted = useFinalizeAttempted();

  const patchRow = (next: BulletinIncidentTime) =>
    setIncidentTimes(
      incidentTimes.map((row) => (row.id === next.id ? next : row)),
    );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {incidentTimes.map((time, index) => (
        <IncidentTimeRow
          key={time.id}
          value={time}
          index={index + 1}
          onChange={patchRow}
          // The row owns its remove button so it sits beside the inputs rather than beside the
          // heading; the section still decides whether removing is allowed.
          removable={incidentTimes.length > 1}
          onRemove={() =>
            setIncidentTimes(incidentTimes.filter((row) => row.id !== time.id))
          }
          errors={
            finalizeAttempted || time.startDate
              ? validateIncidentTime(time)
              : {}
          }
        />
      ))}
      <Box>
        <Button
          size="small"
          startIcon={<Add />}
          onClick={() =>
            setIncidentTimes([...incidentTimes, createIncidentTime()])
          }
        >
          {t("fields.add_time")}
        </Button>
      </Box>
    </Box>
  );
};

const LocationsField: React.FC = () => {
  const { t } = useTranslation("bulletin/create-v2");
  const [locations, setLocations] = useBulletinField("locations");
  const finalizeAttempted = useFinalizeAttempted();

  // A functional update, unlike the other row lists: a location's geocode arrives asynchronously, so
  // the patch has to merge against whatever the current value is rather than the render it started in.
  const patchRow = (id: string, patch: Partial<BulletinLocation>) =>
    setLocations((previous) =>
      previous.map((row) => (row.id === id ? { ...row, ...patch } : row)),
    );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {locations.map((location, index) => (
        <LocationRow
          key={location.id}
          value={location}
          index={index + 1}
          onPatch={(patch) => patchRow(location.id, patch)}
          removable={locations.length > 1}
          onRemove={() =>
            setLocations(locations.filter((row) => row.id !== location.id))
          }
          errors={
            finalizeAttempted || locationRowHasContent(location)
              ? validateLocationRow(location)
              : {}
          }
        />
      ))}
      <Box>
        <Button
          size="small"
          startIcon={<Add />}
          onClick={() => setLocations([...locations, createLocation()])}
        >
          {t("fields.add_location")}
        </Button>
      </Box>
      <Typography variant="caption" color="text.secondary">
        {t("sections.incident_description")}
      </Typography>
    </Box>
  );
};

export const IncidentSection: React.FC = () => (
  <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
    <CrimesField />
    <IncidentTimesField />
    <LocationsField />
  </Box>
);
